import React, { useState, useRef, useEffect } from 'react';
import PrivacyPolicyService from '../../modules/Admin/services/PrivacyPolicyService';
import ToastService from '../../modules/Admin/services/ToastService';
import { Toast } from 'primereact/toast';
import AuthService from '../../modules/Admin/services/AuthService';

const C = {
  ink: '#344767', muted: '#8392ab', border: '#e9ecef',
  primary: '#5e72e4', success: '#2dce89',
  bgSoft: '#f8f9fe', shadow: '0 2px 24px rgba(52,71,103,0.09)',
};

const SECTIONS = [
  {
    icon: 'fas fa-database', color: '#5e72e4', title: 'Information We Collect',
    body: 'We collect information you voluntarily provide — such as your name, email address, and phone number — when you use Next Follow Up. We also collect usage data automatically (IP address, browser type, pages visited) to improve our services.',
  },
  {
    icon: 'fas fa-cog', color: '#fb6340', title: 'How We Use Your Information',
    body: 'Your information is used to operate the CRM platform, respond to your support requests, send service-related notifications, improve product features, and comply with legal obligations. We do not use your data for unsolicited marketing.',
  },
  {
    icon: 'fas fa-share-alt', color: '#2dce89', title: 'Sharing Your Information',
    body: 'We never sell your personal data. We may share it with trusted service providers (hosting, email delivery) strictly to operate our platform. We may also disclose data when required by law or to protect the safety of our users.',
  },
  {
    icon: 'fas fa-shield-alt', color: '#f5365c', title: 'Data Security',
    body: 'We implement industry-standard security measures including encrypted data transmission (HTTPS), access controls, and regular security reviews. While we take every precaution, no system is completely immune to breaches.',
  },
  {
    icon: 'fas fa-user-check', color: '#11cdef', title: 'Your Rights',
    body: 'You may request access to, correction of, or deletion of your personal data at any time. You may also opt out of marketing communications by contacting us. We will respond to all privacy requests within 30 days.',
  },
  {
    icon: 'fas fa-child', color: '#825ee4', title: "Children's Privacy",
    body: 'Next Follow Up is a professional B2B platform not directed at children under 13. We do not knowingly collect data from minors. If you believe a child has provided us with personal information, please contact us immediately.',
  },
  {
    icon: 'fas fa-sync-alt', color: '#344767', title: 'Policy Updates',
    body: 'We may update this Privacy Policy periodically. When we do, we will revise the "Last Updated" date and notify active users via email or in-app notification. Continued use of the platform after changes constitutes acceptance.',
  },
];



export default function Privacy() {
  const [Policy, setPolicy] = useState([]);
  const [policyId, setPolicyId] = useState('');
  const [form, setForm] = useState({ privacypolicytext: '', createddate: '' });
  const [showForm, setShowForm] = useState(false);
  const toast = useRef(null);
  const toastService = new ToastService(toast);
  const userRole = AuthService.getLoggedInRoleId();
  const rid = parseInt(userRole, 10);
  const canEdit = rid === 1 || rid === 2 || rid === 3;

  useEffect(() => {
    PrivacyPolicyService.getAllPolicies().then(setPolicy).catch(() => {});
  }, []);

  const resetForm = () => { setForm({ privacypolicytext: '', createddate: '' }); setPolicyId(''); setShowForm(false); };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (policyId) {
        const fd = new FormData();
        fd.append('privacypolicytext', form.privacypolicytext);
        fd.append('createddate', form.createddate);
        fd.append('id', policyId);
        await PrivacyPolicyService.updatePolicy(policyId, fd);
        toastService.showSuccess('Saved', 'Policy updated.');
      } else {
        await PrivacyPolicyService.createPolicy({ privacypolicytext: form.privacypolicytext, createddate: form.createddate });
        toastService.showSuccess('Saved', 'Policy entry added.');
      }
      resetForm();
      const updated = await PrivacyPolicyService.getAllPolicies();
      setPolicy(updated);
    } catch { toastService.showError('Error', 'Failed to save policy.'); }
  };

  return (
    <div style={{ background: C.bgSoft, minHeight: '100vh' }}>
      <Toast ref={toast} />

      {/* hero */}
      <div style={{ background: 'linear-gradient(135deg,#344767 0%,#21243d 100%)', padding: '52px 24px 68px', textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', width: 64, height: 64, borderRadius: 18, background: 'rgba(255,255,255,0.12)', alignItems: 'center', justifyContent: 'center', fontSize: 26, color: '#fff', marginBottom: 18 }}>
          <i className="fas fa-user-shield" />
        </div>
        <h1 style={{ margin: '0 0 10px', fontSize: 32, fontWeight: 800, color: '#fff' }}>Privacy Policy</h1>
        <p style={{ margin: '0 auto', maxWidth: 500, fontSize: 14, color: 'rgba(255,255,255,0.72)', lineHeight: 1.6 }}>
          Next Follow Up is committed to protecting your personal data. This policy explains what we collect and how we use it.
        </p>
        <p style={{ margin: '14px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Last updated: May 2026</p>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 20px 60px' }}>

        {/* intro card */}
        <div style={{ background: '#fff', borderRadius: 18, boxShadow: C.shadow, padding: '28px 32px', marginTop: -28, marginBottom: 28 }}>
          <p style={{ margin: 0, fontSize: 15, color: C.muted, lineHeight: 1.7 }}>
            By using Next Follow Up, you agree to the practices described in this Privacy Policy. This policy applies to all users of the platform, including Super Admins, Agency Admins, and Employees.
          </p>
        </div>

        {/* sections */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 32 }}>
          {SECTIONS.map(({ icon, color, title, body }) => (
            <div key={title} style={{ background: '#fff', borderRadius: 16, boxShadow: C.shadow, padding: '22px 26px', display: 'flex', gap: 18, alignItems: 'flex-start' }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color, flexShrink: 0 }}>
                <i className={icon} />
              </div>
              <div>
                <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 800, color: C.ink }}>{title}</h3>
                <p style={{ margin: 0, fontSize: 14, color: C.muted, lineHeight: 1.7 }}>{body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* contact */}
        <div style={{ background: 'linear-gradient(135deg,#5e72e4,#825ee4)', borderRadius: 18, padding: '28px 32px', textAlign: 'center', marginBottom: 32 }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 800, color: '#fff' }}>Questions about this policy?</h3>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>Our team is happy to help with any privacy-related questions.</p>
          <a href="mailto:support@nextfollowup.in"
            style={{ background: '#fff', color: C.primary, borderRadius: 10, padding: '10px 22px', fontWeight: 700, fontSize: 13, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <i className="fas fa-envelope" /> support@nextfollowup.in
          </a>
        </div>

        {/* admin: dynamic policy entries */}
        {canEdit && (
          <div style={{ background: '#fff', borderRadius: 18, boxShadow: C.shadow, overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg,#344767,#21243d)', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <i className="fas fa-edit" style={{ color: '#fff', fontSize: 15 }} />
                <span style={{ fontWeight: 800, fontSize: 15, color: '#fff' }}>Manage Policy Entries</span>
              </div>
              <button onClick={() => { setShowForm(!showForm); setPolicyId(''); setForm({ privacypolicytext: '', createddate: '' }); }}
                style={{ background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.3)', borderRadius: 9, padding: '6px 14px', fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <i className={`fas fa-${showForm ? 'times' : 'plus'}`} /> {showForm ? 'Cancel' : 'Add Entry'}
              </button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              {(showForm || policyId) && (
                <form onSubmit={onSubmit} style={{ background: C.bgSoft, borderRadius: 12, padding: '20px', marginBottom: 20, border: `1px solid ${C.border}` }}>
                  <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 800, color: C.ink }}>{policyId ? 'Edit Entry' : 'New Entry'}</h4>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Policy Text</label>
                    <textarea rows={4} name="privacypolicytext" value={form.privacypolicytext} onChange={(e) => setForm(p => ({ ...p, privacypolicytext: e.target.value }))} required
                      style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${C.border}`, borderRadius: 10, fontSize: 14, color: C.ink, boxSizing: 'border-box', outline: 'none', resize: 'vertical' }} />
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button type="submit"
                      style={{ background: 'linear-gradient(135deg,#5e72e4,#825ee4)', border: 'none', borderRadius: 9, padding: '9px 20px', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}>
                      <i className="fas fa-save" /> {policyId ? 'Update' : 'Save'}
                    </button>
                    <button type="button" onClick={resetForm}
                      style={{ background: '#fff', border: `1.5px solid ${C.border}`, borderRadius: 9, padding: '9px 18px', color: C.muted, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}
              {Policy.length === 0 && <p style={{ margin: 0, color: C.muted, fontSize: 13 }}>No custom policy entries yet.</p>}
              {Policy.map((p) => (
                <div key={p.id} style={{ borderBottom: `1px solid ${C.border}`, padding: '14px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 4px', fontSize: 14, color: C.ink, lineHeight: 1.6 }}>{p.privacyPolicyText}</p>
                    <p style={{ margin: 0, fontSize: 11, color: C.muted }}>{new Date(p.createdDate).toLocaleString()}</p>
                  </div>
                  <button onClick={() => { setForm({ privacypolicytext: p.privacyPolicyText, createddate: p.createdDate }); setPolicyId(p.id); setShowForm(false); }}
                    style={{ background: `${C.primary}12`, border: 'none', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: C.primary, flexShrink: 0 }}>
                    <i className="fas fa-pen" style={{ fontSize: 12 }} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
